import { type ReactNode, useMemo, useState } from "react";

import { COLORS, SEQUENTIAL } from "../tokens";
import { EChart, type EChartOption } from "./EChart";

// =================================================================================================
// DynamicChart — a categorical bar chart with SWITCHABLE dimension + metric (the "dynamic, not
// static" ask). The caller supplies the raw rows plus the dimension/metric option lists; the chart
// renders a `<MetricSwitcher>` (which metric to plot) and a `<DimensionPicker>` (which category axis
// to group by) and recomputes the bars on change. Color is the neutral/brand SEQUENTIAL ramp by
// default — health series must opt into a RAG color explicitly via `color`, keeping "RAG = health
// only" intact at this layer.
//
// It is deliberately data-shape-agnostic: a row is `Record<string, string | number | null>`, and an
// option just names the field key + label (+ unit for the tooltip). One reusable control set powers
// every switchable chart across the hub.
// =================================================================================================

export interface ChartDimensionOption {
  // Field key on each row to group/label by (the x-axis category).
  readonly key: string;
  readonly label: string;
}

export interface ChartMetricOption {
  // Field key on each row holding the numeric value to plot (the y value).
  readonly key: string;
  readonly label: string;
  // Optional unit suffix for the tooltip (e.g. "MB", "%", "USD").
  readonly unit?: string;
}

export type DynamicChartRow = Readonly<Record<string, string | number | null>>;

export interface DynamicChartProps {
  readonly rows: readonly DynamicChartRow[];
  readonly dimensions: readonly ChartDimensionOption[];
  readonly metrics: readonly ChartMetricOption[];
  // Initial selections (default to the first of each list).
  readonly initialDimensionKey?: string;
  readonly initialMetricKey?: string;
  // Override the bar color (pass a RAG color ONLY for a genuine health metric).
  readonly color?: string;
  readonly height?: number;
  // Notified when the user switches metric/dimension (e.g. so a parent can re-fetch).
  readonly onChange?: (dimensionKey: string, metricKey: string) => void;
}

const cellNumber = (v: string | number | null): number => (typeof v === "number" ? v : 0);

const cellLabel = (v: string | number | null): string => (v === null ? "—" : String(v));

export const DynamicChart = ({
  rows,
  dimensions,
  metrics,
  initialDimensionKey,
  initialMetricKey,
  color,
  height = 280,
  onChange,
}: DynamicChartProps): ReactNode => {
  const [dimensionKey, setDimensionKey] = useState<string>(
    initialDimensionKey ?? dimensions[0]?.key ?? "",
  );
  const [metricKey, setMetricKey] = useState<string>(initialMetricKey ?? metrics[0]?.key ?? "");

  const activeMetric = metrics.find((m) => m.key === metricKey) ?? metrics[0];

  const emit = (dim: string, met: string): void => {
    onChange?.(dim, met);
  };

  const option: EChartOption = useMemo(() => {
    const categories = rows.map((r) => cellLabel(r[dimensionKey] ?? null));
    const values = rows.map((r) => cellNumber(r[metricKey] ?? null));
    const unit = activeMetric?.unit ? ` ${activeMetric.unit}` : "";
    return {
      tooltip: {
        trigger: "axis",
        valueFormatter: (v: number | string) => `${Number(v).toLocaleString()}${unit}`,
        backgroundColor: COLORS.surface2,
        borderColor: COLORS.border,
        textStyle: { color: COLORS.textPrimary },
      },
      grid: { left: 8, right: 16, top: 16, bottom: 24, containLabel: true },
      xAxis: {
        type: "category",
        data: categories,
        axisLabel: { color: COLORS.textMuted, fontSize: 11 },
        axisLine: { lineStyle: { color: COLORS.border } },
      },
      yAxis: {
        type: "value",
        axisLabel: { color: COLORS.textMuted, fontSize: 11 },
        splitLine: { lineStyle: { color: COLORS.border } },
      },
      series: [
        {
          type: "bar",
          data: values,
          itemStyle: { color: color ?? SEQUENTIAL[SEQUENTIAL.length - 1] },
          barMaxWidth: 40,
        },
      ],
    };
  }, [rows, dimensionKey, metricKey, activeMetric, color]);

  return (
    <div className="dynamic-chart">
      <div className="dynamic-chart-controls">
        {metrics.length > 1 ? (
          <MetricSwitcher
            options={metrics}
            value={metricKey}
            onChange={(k) => {
              setMetricKey(k);
              emit(dimensionKey, k);
            }}
          />
        ) : null}
        {dimensions.length > 1 ? (
          <DimensionPicker
            options={dimensions}
            value={dimensionKey}
            onChange={(k) => {
              setDimensionKey(k);
              emit(k, metricKey);
            }}
          />
        ) : null}
      </div>
      <EChart option={option} height={height} />
    </div>
  );
};

// ── The reusable picker controls (exported so surfaces can compose them standalone too) ──────────

export interface MetricSwitcherProps {
  readonly options: readonly ChartMetricOption[];
  readonly value: string;
  readonly onChange: (key: string) => void;
}

// A labeled <select> that switches which METRIC a chart plots.
export const MetricSwitcher = ({ options, value, onChange }: MetricSwitcherProps): ReactNode => (
  <label className="chart-switcher">
    <span className="chart-switcher-label">Metric</span>
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => (
        <option key={o.key} value={o.key}>
          {o.label}
          {o.unit ? ` (${o.unit})` : ""}
        </option>
      ))}
    </select>
  </label>
);

export interface DimensionPickerProps {
  readonly options: readonly ChartDimensionOption[];
  readonly value: string;
  readonly onChange: (key: string) => void;
}

// A labeled <select> that switches which DIMENSION (category axis) a chart groups by.
export const DimensionPicker = ({ options, value, onChange }: DimensionPickerProps): ReactNode => (
  <label className="chart-switcher">
    <span className="chart-switcher-label">By</span>
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => (
        <option key={o.key} value={o.key}>
          {o.label}
        </option>
      ))}
    </select>
  </label>
);
